<template>
  <div :class="noticeClass" @click="$emit('open')">
    <div class="message">
      <slot />
    </div>
    <div class="right">
      <span v-if="!notice.viewed" class="unread-dot" aria-label="Unread" />
      <span class="time">{{ relativeTime }}</span>
      <BaseButton
        kind="auxiliary"
        class="view-toggle-button"
        :tooltip="notice.viewed ? 'Mark as unread' : 'Mark as read'"
        tooltip-display="always"
        @click.stop="toggleViewed"
      >
        <template #icon>
          <UnreadIcon v-if="notice.viewed" />
          <ReadIcon v-else />
        </template>
      </BaseButton>
      <BaseButton
        kind="auxiliary"
        class="archive-button"
        tooltip="Archive"
        tooltip-display="always"
        @click.stop="archive"
      >
        <template #icon>
          <ArchiveIcon />
        </template>
      </BaseButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

import type { FullNotice } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import ArchiveIcon from "@/components/icons/ArchiveIcon.vue";
import ReadIcon from "@/components/icons/ReadIcon.vue";
import UnreadIcon from "@/components/icons/UnreadIcon.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  name: "NoticeBase",
  components: { ArchiveIcon, BaseButton, ReadIcon, UnreadIcon },
  props: {
    notice: {
      type: Object as PropType<FullNotice>,
      required: true,
    },
    relativeTime: {
      type: String,
      required: true,
    },
    noticeClass: {
      type: String,
      required: true,
    },
  },
  emits: {
    open: () => true,
    archived: (_noticeId: string) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();

    const toggleViewed = async () => {
      if (props.notice.viewed) {
        await controller.notice.markUnviewed(props.notice);
      } else {
        await controller.notice.markViewed(props.notice);
      }
    };

    const archive = async () => {
      await controller.notice.markArchived(props.notice);
      emit("archived", props.notice.id);
    };

    return {
      archive,
      toggleViewed,
    };
  },
});
</script>

<style scoped lang="scss">
// Base notice container - applies to all notice types
.inbox-mention,
.inbox-set-relation-notice,
.inbox-error-notice,
.inbox-generic-notice,
.inbox-automation-ask-user-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-inline-start: -16px;
  padding-block: 4px;
  padding-inline-end: 12px;
  padding-inline-start: 17px;
  border-radius: $border-radius-m;
  @include transition(background-color 100ms ease-out);

  @include themed() {
    background-color: t($bg);
  }

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }
}

.message {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inbox-error-notice {
  .message {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.right {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  @include themed() {
    color: t($grey-50);
  }
}

.time {
  @include themed() {
    color: t($grey-70);
  }
}

:deep(button.base-button.archive-button) {
  flex: 0 0 auto;

  &:hover {
    @include themed() {
      background-color: t($grey-15);
    }
  }
}

:deep(button.base-button.view-toggle-button) {
  flex: 0 0 auto;

  &:hover {
    @include themed() {
      background-color: t($grey-15);
    }
  }
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 8px;
  @include themed() {
    background-color: t($secondary-50);
  }
}
</style>
